/**
 * Translation lookup.
 *
 * No user-facing string is written in a component. `t` takes a MessageKey and
 * nothing else, which is what makes the typecheck able to prove that every
 * string on every page exists in both languages.
 */

import { createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react'
import { DEFAULT_LOCALE, type Locale } from '../../shared/locale.ts'
import type { MessageKey } from '../../shared/message-keys.ts'
import { bg } from './bg.ts'
import { currentLocale, rememberLocale } from './current-locale.ts'
import { en } from './en.ts'

const TABLES: Record<Locale, Record<MessageKey, string>> = { bg, en }

interface I18n {
  locale: Locale
  t: (key: MessageKey) => string
  setLocale: (locale: Locale) => void
}

const I18nContext = createContext<I18n | null>(null)

const noSubscription = () => () => {}
const serverLocale = (): Locale => DEFAULT_LOCALE

export function I18nProvider({ children }: { children: ReactNode }) {
  /**
   * The reader's stored or linked choice, read so that hydration matches.
   *
   * The server cannot see `?lang=` stored earlier or `localStorage`, so it
   * always renders Bulgarian. Hydrating that HTML with an English first render
   * is a mismatch: React throws error #418, discards the server's page and
   * draws it again. With a server snapshot of Bulgarian, React hydrates in
   * Bulgarian and switches to the reader's choice straight after, with no
   * error. A page that is not hydrated (no seed) reads the choice at once.
   */
  const stored = useSyncExternalStore(noSubscription, currentLocale, serverLocale)
  // A choice made with the switcher. Kept in state, not only in storage,
  // because a write to storage can fail (see rememberLocale) and the switcher
  // must work anyway.
  const [chosen, setChosen] = useState<Locale | null>(null)
  const locale = chosen ?? stored

  /**
   * `<html lang>` follows the locale, from the first render and not only when
   * the switcher runs.
   *
   * It used to be assigned inside `setLocale`, which meant it was only ever
   * corrected by a reader who used the switcher: anybody arriving on the
   * English interface through `?lang=en` or a stored preference read the whole
   * visit with `lang="bg"` from `index.html`. That is not cosmetic. It is what a
   * screen reader consults to choose pronunciation, and English announced with
   * Bulgarian phonetics is a real problem for the reader who depends on it. It
   * also tells a crawler the page is Bulgarian, which is wrong for the English
   * half of a bilingual site.
   *
   * Derived here rather than assigned at each place the locale changes, so there
   * is one rule and no second place to forget. `index.html` ships `lang="bg"`,
   * which is now the correct default, so the common case has nothing to correct
   * and there is no flash before this runs.
   */
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const value = useMemo<I18n>(
    () => ({
      locale,
      t: (key) => TABLES[locale][key],
      setLocale: (next) => {
        // Persisted first, because that is what the auth client reads when it
        // tells the Worker which language to write mail in. The `lang`
        // attribute is not touched here — the effect above owns it.
        rememberLocale(next)
        setChosen(next)
      },
    }),
    [locale],
  )

  return <I18nContext value={value}>{children}</I18nContext>
}

export function useI18n(): I18n {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useI18n outside I18nProvider')
  return value
}
